import { Controller } from "@hotwired/stimulus"
import { replaceOptions, setDisabled } from "../shared/multi_select"

export default class extends Controller {
  static targets = ["department", "error"]
  static values = { url: String, errorMessage: String }

  connect() {
    this.form = this.departmentTarget.form
    this.formAbortController = new AbortController()
    this.form.addEventListener("submit", (event) => {
      if (!this.departmentTarget.disabled) return

      event.preventDefault()
      event.stopImmediatePropagation()
    }, { capture: true, signal: this.formAbortController.signal })
  }

  disconnect() {
    this.departmentsAbortController?.abort()
    this.formAbortController.abort()
    this.setSubmissionBlocked(false)
    this.pendingDepartments = null
  }

  changeCompany(event) {
    this.refreshDepartments(event.currentTarget.form)
  }

  changeDates(event) {
    const form = event.currentTarget.form
    if (!["start_date", "end_date"].every((name) => form.elements.namedItem(name).validity.valid)) return

    this.refreshDepartments(form, true)
  }

  async refreshDepartments(form, preserveSelection = false) {
    const companyCode = form.elements.namedItem("company_code").value
    const previous = preserveSelection
      ? (this.pendingDepartments?.companyCode === companyCode
          ? this.pendingDepartments.codes
          : Array.from(this.departmentTarget.selectedOptions, (option) => option.value))
      : []
    this.pendingDepartments = { companyCode, codes: previous }
    this.departmentsAbortController?.abort()
    const request = new AbortController()
    this.departmentsAbortController = request
    this.errorTarget.hidden = true
    this.setSubmissionBlocked(true)
    this.setDepartmentTree([])
    setDisabled(this.departmentTarget, true)
    replaceOptions(this.departmentTarget, [])

    if (!companyCode) return

    const url = new URL(this.urlValue, window.location.origin)
    url.searchParams.set("company_code", companyCode)
    for (const name of ["start_date", "end_date"]) {
      url.searchParams.set(name, form.elements.namedItem(name).value)
    }
    previous.forEach((code) => url.searchParams.append("department_codes[]", code))

    try {
      const response = await fetch(url, {
        credentials: "same-origin",
        headers: { Accept: "application/json", "X-Requested-With": "XMLHttpRequest" },
        signal: request.signal
      })
      const payload = await response.json()
      if (request.signal.aborted) return
      if (!response.ok) throw new Error(payload.error || this.errorMessageValue)

      this.setDepartmentTree(payload.tree)
      replaceOptions(this.departmentTarget, payload.departments)
      setDisabled(this.departmentTarget, payload.departments.length === 0)
      this.setSubmissionBlocked(this.departmentTarget.disabled)
    } catch (error) {
      if (request.signal.aborted) return

      this.errorTarget.textContent = this.errorMessageValue
      this.errorTarget.hidden = false
    } finally {
      if (!request.signal.aborted) this.pendingDepartments = null
    }
  }

  setSubmissionBlocked(blocked) {
    if (blocked) {
      this.submitButtons ||= Array.from(this.form.querySelectorAll('button[type="submit"], input[type="submit"]'),
        (button) => ({ button, disabled: button.disabled }))
      this.submitButtons.forEach(({ button }) => { button.disabled = true })
    } else {
      this.submitButtons?.forEach(({ button, disabled }) => { button.disabled = disabled })
      this.submitButtons = null
    }
  }

  setDepartmentTree(tree) {
    const config = JSON.parse(this.departmentTarget.dataset.coreuiConfig)
    config.tree = tree
    this.departmentTarget.dataset.coreuiConfig = JSON.stringify(config)
  }
}
